import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { useChatStore } from "@/stores/useChatStore";
import { useCurrentUserStore } from "@/stores/useCurrentUserStore";
import { useToastStore } from "@/stores/useToastStore";
import { loadChat, listChats } from "@/lib/api";
import { filtersRevealingChat } from "@/lib/chat-list-filters";
import { useAgentSessionContext } from "@/components/chat/AgentSessionContext";

/**
 * Keeps the URL (`/chats/:chatId`) and the chat session store in sync.
 *
 * - Loads the chat list once on mount.
 * - When `:chatId` is present, loads that chat (flipping the visibility filter
 *   to `"all"` if it's hidden by the `"mine"` filter). Redirects to
 *   `/chats/new` if the chat doesn't exist.
 * - On `/chats/new`, shows an empty new-chat draft without creating a chat in
 *   the backend; the chat is created on the first message (CenterPane).
 * - When no `:chatId` is present, falls back to the most recent chat, or shows
 *   a new-chat draft if there are none.
 *
 * Renders nothing; mount it inside `AgentSessionProvider`.
 */
export function ChatUrlSync() {
	const navigate = useNavigate();
	const { chatId: urlChatId } = useParams<{ chatId: string }>();
	const pathname = useLocation().pathname;
	const isNewChatRoute = pathname === "/chats/new";
	// チャット系のルート (`/` と `/chats/...`) のみチャットの復元・リダイレクトを行う。
	// それ以外のメインビュー (プロジェクト・設定等) では URL がチャットと
	// 無関係なため、チャットへのリダイレクトを抑止する。
	const isChatRoute = pathname === "/" || pathname.startsWith("/chats");
	const currentUserId = useCurrentUserStore((s) => s.id);
	const { switchToChat, startNewChatDraft } = useAgentSessionContext();

	const listLoadedRef = useRef(false);
	const [chatListLoaded, setChatListLoaded] = useState(false);

	useEffect(() => {
		if (listLoadedRef.current) return;
		listLoadedRef.current = true;
		(async () => {
			try {
				const chats = await listChats();
				useChatStore.getState().setChatList(chats);
			} finally {
				setChatListLoaded(true);
			}
		})();
	}, []);

	useEffect(() => {
		if (!chatListLoaded) return;
		// チャット以外のビュー表示中はチャットの復元・リダイレクトを行わない。チャットに戻る際は
		// サイドバーが `/` へ遷移し、そのときにこのエフェクトが走って復元される。
		if (!isChatRoute) return;
		const store = useChatStore.getState();

		// 切り替えが速いと非同期処理 (loadChat) の解決順が前後し、後から解決した古い
		// 切り替えが最新の表示を上書きしてしまう。エフェクトの再実行・アンマウント時に
		// このフラグを立て、await のたびに確認することで last-click-wins を保証する。
		let cancelled = false;

		if (isNewChatRoute) {
			// 新規チャットの下書き表示。直接遷移やリロードで別チャットを開いたままの
			// 場合に備えて、下書き状態へリセットする (LeftPane 経由なら既にリセット済み)。
			if (store.chatId !== null) startNewChatDraft();
			return;
		}

		if (urlChatId) {
			if (store.chatId === urlChatId) return;
			(async () => {
				// サイドバーのチャットはメタデータがローカルの chatList に既にあるため、
				// メタデータ取得のネットワーク往復を挟まず同期的に切り替える。これにより
				// クリックと同じ描画フレームでスケルトンが表示され、切り替えが即座に感じられる。
				// ローカルに無いチャット (ディープリンク等) のみ loadChat で取得する。
				const data = store.chatList.find((c) => c.id === urlChatId) ?? (await loadChat(urlChatId));
				if (cancelled) return;
				if (data === null) {
					useToastStore.getState().show({
						type: "error",
						message: "指定されたチャットが見つかりませんでした。新規チャットを開きました。",
					});
					navigate("/chats/new", { replace: true });
					return;
				}
				// 直接開いたチャットが絞り込みで隠れていれば、隠している絞り込みだけ緩めて一覧に出す。
				const filters = useChatStore.getState().chatListFilters;
				const revealing = filtersRevealingChat(filters, data, currentUserId);
				if (revealing !== filters) useChatStore.getState().setChatListFilters(revealing);
				await switchToChat(data);
			})();
			return () => {
				cancelled = true;
			};
		}

		if (store.chatId) {
			navigate(`/chats/${store.chatId}`, { replace: true });
			return;
		}
		(async () => {
			const chats = useChatStore.getState().chatList;
			if (chats.length > 0) {
				const most = chats[0];
				const data = await loadChat(most.id);
				if (cancelled) return;
				if (data) {
					await switchToChat(data);
					if (cancelled) return;
					navigate(`/chats/${most.id}`, { replace: true });
				}
			} else {
				// チャットが無いときは空のチャットを作らず、新規チャットの下書きを表示する。
				navigate("/chats/new", { replace: true });
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [
		urlChatId,
		isNewChatRoute,
		isChatRoute,
		chatListLoaded,
		currentUserId,
		navigate,
		switchToChat,
		startNewChatDraft,
	]);

	return null;
}
